import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { payrollAPI, caregiverProfilesAPI } from '../services/api';
import BillingRunsTab from '../components/billing/BillingRunsTab';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import { CurrencyDollar, Clock, CalendarBlank, UserCircle, FunnelSimple, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';

const PayrollPage = () => {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get('tab') === 'runs' ? 'runs' : 'report');
  const { user } = useAuth();
  const [payroll, setPayroll] = useState(null);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 14), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [searchTerm, setSearchTerm] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const openCaregiverDetail = async (caregiverId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await payrollAPI.getCaregiverPayroll(caregiverId, { start_date: startDate, end_date: endDate });
      setDetail(res.data);
    } catch (e) { toast.error('Failed to load caregiver shifts'); setDetailOpen(false); }
    finally { setDetailLoading(false); }
  };

  useEffect(() => {
    loadCaregivers();
    loadPayroll();
  }, []);

  const loadCaregivers = async () => {
    try { const res = await caregiverProfilesAPI.getAll(); setCaregivers(res.data); } catch (e) {}
  };

  const loadPayroll = async () => {
    setLoading(true);
    try {
      const res = await payrollAPI.getAgencyPayroll({ start_date: startDate, end_date: endDate });
      setPayroll(res.data);
    } catch (error) { toast.error('Failed to load payroll'); }
    finally { setLoading(false); }
  };

  const filteredItems = payroll?.line_items?.filter(item =>
    !searchTerm || item.caregiver_name.toLowerCase().includes(searchTerm.toLowerCase()) || item.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (user?.role !== 'supervisor' && user?.role !== 'super_admin') {
    return <div className="flex flex-col items-center justify-center h-64"><CurrencyDollar weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" /><h3 className="font-heading text-lg">Access Restricted</h3></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="payroll-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-semibold">Payroll</h1>
        <p className="text-muted-foreground mt-1">Track caregiver hours and pay</p>
      </div>

      <div className="flex gap-1 rounded-xl bg-muted p-1 w-fit" data-testid="payroll-tabs">
        <button onClick={() => setTab('report')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'report' ? 'bg-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-payroll-report">Payroll Report</button>
        <button onClick={() => setTab('runs')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'runs' ? 'bg-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-pay-runs">Weekly Pay Runs</button>
      </div>

      {tab === 'runs' ? <BillingRunsTab kind="payroll" /> : (<>

      {/* Filters */}
      <Card className="rounded-2xl border border-border" data-testid="payroll-filters">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="space-y-1 flex-1">
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-xl" data-testid="payroll-start-date" />
            </div>
            <div className="space-y-1 flex-1">
              <Label className="text-xs">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-xl" data-testid="payroll-end-date" />
            </div>
            <Button className="rounded-xl gap-2" onClick={loadPayroll} disabled={loading} data-testid="payroll-apply-filter">
              <FunnelSimple weight="bold" className="w-4 h-4" /> {loading ? 'Loading...' : 'Apply'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {payroll && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="rounded-2xl border border-border" data-testid="payroll-total-pay">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total Payroll</p>
                  <h3 className="font-heading text-3xl font-semibold mt-2">${payroll.total_payroll.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <CurrencyDollar weight="duotone" className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total Hours</p>
                  <h3 className="font-heading text-3xl font-semibold mt-2">{payroll.total_hours}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Clock weight="duotone" className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total Visits</p>
                  <h3 className="font-heading text-3xl font-semibold mt-2">{payroll.total_visits}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CalendarBlank weight="duotone" className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Caregiver Breakdown */}
      {payroll?.caregiver_breakdown?.length > 0 && (
        <Card className="rounded-2xl border border-border" data-testid="caregiver-breakdown-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Caregiver Breakdown</CardTitle>
            <CardDescription>Pay summary per caregiver</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {payroll.caregiver_breakdown.map((cb, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-xl bg-muted/30 cursor-pointer hover:bg-muted/60 transition-colors"
                  onClick={() => openCaregiverDetail(cb.caregiver_id)}
                  data-testid={`caregiver-breakdown-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <UserCircle weight="duotone" className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-blue-700 underline underline-offset-2 decoration-blue-300 hover:decoration-blue-700" data-testid={`caregiver-breakdown-name-${i}`}>{cb.caregiver_name}</p>
                      <p className="text-xs text-muted-foreground">{cb.visit_count} visits &middot; {cb.total_hours} hours ({cb.regular_hours} reg / {cb.overtime_hours} OT) &middot; ${cb.pay_rate}/hr</p>
                    </div>
                  </div>
                  <p className="font-heading font-semibold text-lg text-blue-700">${cb.total_pay.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line Items Table */}
      <Card className="rounded-2xl border border-border" data-testid="payroll-line-items">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg">Payroll Details</CardTitle>
              <CardDescription>{filteredItems.length} entries</CardDescription>
            </div>
            <div className="relative w-64">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 rounded-xl text-sm" data-testid="payroll-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredItems.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">No payroll entries found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Caregiver</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Pay</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item, i) => (
                    <TableRow key={`${item.visit_id}-${i}`} className={i % 2 === 0 ? '' : 'bg-muted/30'}>
                      <TableCell className="text-sm">{item.date}</TableCell>
                      <TableCell className="text-sm font-medium">{item.caregiver_name}</TableCell>
                      <TableCell className="text-sm">{item.client_name}</TableCell>
                      <TableCell className="text-sm text-right">{item.hours}</TableCell>
                      <TableCell className="text-sm text-right">
                        ${item.rate.toFixed(2)}
                        {item.pay_rate_override != null && (
                          <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">override</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`rounded-full text-xs ${
                          item.rate_type === 'override' ? 'bg-blue-100 text-blue-800' :
                          item.rate_type === 'weekend' ? 'bg-purple-100 text-purple-800' :
                          item.rate_type === 'holiday' ? 'bg-red-100 text-red-800' :
                          'bg-green-100 text-green-800'
                        }`}>{item.rate_type}</Badge>
                        {item.bonus_amount ? (
                          <div className="mt-1 text-[10px] uppercase tracking-wider text-amber-700 font-semibold" title={item.bonus_reason || ''}>
                            +${Number(item.bonus_amount).toFixed(2)} bonus
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-sm text-right font-medium">${item.amount.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Caregiver Shift Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="caregiver-payroll-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl" data-testid="caregiver-detail-name">
              {detailLoading ? 'Loading...' : detail?.caregiver_name || 'Caregiver Shifts'}
            </DialogTitle>
            <DialogDescription>
              Shifts worked {startDate} to {endDate} — cross-reference with billing
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading shifts...</div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-blue-50 border border-blue-100" data-testid="caregiver-detail-total-hours">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-700">Total Hours</p>
                  <p className="font-heading text-xl font-semibold text-blue-900 mt-1">{detail.total_hours}</p>
                </div>
                <div className="p-3 rounded-xl bg-green-50 border border-green-100" data-testid="caregiver-detail-total-pay">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-green-700">Total Pay</p>
                  <p className="font-heading text-xl font-semibold text-green-900 mt-1">${Number(detail.total_pay).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="p-3 rounded-xl bg-muted/40 border border-border" data-testid="caregiver-detail-total-visits">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Shifts</p>
                  <p className="font-heading text-xl font-semibold mt-1">{detail.total_visits}</p>
                </div>
              </div>

              {detail.line_items?.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">No shifts in this period</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="caregiver-detail-shifts-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Clock In</TableHead>
                        <TableHead>Clock Out</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Pay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.line_items.map((item, i) => (
                        <TableRow key={`${item.visit_id}-${i}`} className={i % 2 === 0 ? '' : 'bg-muted/30'}>
                          <TableCell className="text-sm whitespace-nowrap">{item.date}</TableCell>
                          <TableCell className="text-sm font-medium">{item.client_name}</TableCell>
                          <TableCell className="text-sm whitespace-nowrap">{item.check_in ? format(new Date(item.check_in), 'h:mm a') : '—'}</TableCell>
                          <TableCell className="text-sm whitespace-nowrap">{item.check_out ? format(new Date(item.check_out), 'h:mm a') : '—'}</TableCell>
                          <TableCell className="text-sm text-right">{item.hours}</TableCell>
                          <TableCell className="text-sm text-right">
                            ${Number(item.rate).toFixed(2)}
                            {item.pay_rate_override != null && (
                              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">override</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={`rounded-full text-xs ${
                              item.rate_type === 'override' ? 'bg-blue-100 text-blue-800' :
                              item.rate_type === 'weekend' ? 'bg-purple-100 text-purple-800' :
                              item.rate_type === 'holiday' ? 'bg-red-100 text-red-800' :
                              item.rate_type?.includes('overtime') ? 'bg-orange-100 text-orange-800' :
                              'bg-green-100 text-green-800'
                            }`}>{item.rate_type}</Badge>
                            {item.bonus_amount ? (
                              <div className="mt-1 text-[10px] uppercase tracking-wider text-amber-700 font-semibold" title={item.bonus_reason || ''}>
                                +${Number(item.bonus_amount).toFixed(2)} bonus
                              </div>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-sm text-right font-medium">${Number(item.amount).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
};

export default PayrollPage;
